export const initVideoPlayers = () => {
	const els = document.querySelectorAll('.js-video-player');

	for (const el of els) {
		void initVideoPlayer(el);
	}
};

/**
 * An interface to control a video from any source or provider.
 * @typedef {{
 *   el: HTMLElement;
 *   containerEl: HTMLElement;
 *   loaded: Promise<void>;
 *   play: () => void;
 *   pause: () => void;
 * }} Video
 */

/**
 * Initializes a YouTube player component.
 * @param {HTMLElement} el - Video player element
 */
const initVideoPlayer = async (el) => {
	const playBtnEl = el.querySelector('.js-video-player-play-btn');

	if (!playBtnEl) {
		throw new Error('Missing video player elements or attributes');
	}

	const video = await createYoutubeVideo({
		containerEl: el,
		onPlay: (video) => {
			hideThumbnail(video);
		},
		onPause: (video) => {
			showThumbnail(video);
		}
	});

	// now wait for the video to load

	await video.loaded;

	el.dataset.videoLoaded = 'true';

	playBtnEl.addEventListener('click', video.play);
};

/**
 * Shows the video thumbnail.
 * @param {Video} video - Video
 */
const showThumbnail = (video) => {
	video.el.inert = true;
	video.containerEl.dataset.videoThumbnailVisible = 'true';
};

/**
 * Hides the video thumbnail.
 * @param {Video} video - Video
 */
const hideThumbnail = (video) => {
	video.el.inert = false;
	video.containerEl.dataset.videoThumbnailVisible = 'false';
};

/**
 * Creates a YouTube video and returns a `Video` instance.
 * @param {{
 *   containerEl: HTMLElement;
 *   onPlay?: (video: Video) => void | Promise<void>;
 *   onPause?: (video: Video) => void | Promise<void>;
 * }} config - Configuration
 * @returns {Promise<Video>} Video
 */
const createYoutubeVideo = async (config) => {
	const
		placeholderEl = config.containerEl.querySelector('.js-video-player-youtube-placeholder'),
		id = placeholderEl?.dataset.youtubeId;

	if (
		!placeholderEl
		|| !id
	) {
		throw new Error('Missing YouTube player elements or attributes');
	}

	return new Promise(
		(resolve) => {
			const create = () => {
				const player = new YT.Player(placeholderEl, {
					width: 640,
					height: 360,
					videoId: id,
					playerVars: {
						enablejsapi: 1,
						modestbranding: 1,
						rel: 0,
						showinfo: 0
					},
					host: 'https://www.youtube-nocookie.com'
				});

				const el = player.getIframe();

				el.inert = true;

				const loaded = new Promise(
					(resolve) => {
						const handler = () => {
							player.removeEventListener('onReady', handler);
							resolve();
						};

						player.addEventListener('onReady', handler); // event listener options don't exist
					}
				);

				/** @type {Video} */
				const video = {
					el,
					containerEl: config.containerEl,
					loaded,
					play: () => {
						player.playVideo();
					},
					pause: () => {
						player.pauseVideo();
					}
				};

				const onStateChangeHandler = (ev) => {
					switch (ev.data) {
						case 1:
							config.onPlay?.(video);
							return;
						case 2:
							config.onPause?.(video);
							return;
						default:
							return;
					}
				};

				player.addEventListener('onStateChange', onStateChangeHandler);

				resolve(video);
			};

			const load = async () => {
				await youtubeApiReady;
				create();
			};

			void load();
		}
	);
};

/**
 * A promise that resolves if/once the YouTube iframe API has loaded.
 * @type {Promise<void>}
 */
const youtubeApiReady = new Promise(
	(resolve) => {
		if ('YT' in window && 'Player' in YT) {
			resolve();
			return;
		}

		window.onYouTubeIframeAPIReady = () => {
			resolve();
		};
	}
);
